import { PLAYER_MAX_HP, PLAYER_RESPAWN_SECONDS } from '../net/protocol';

export interface PlayerHealthEvents {
  /** Životy se změnily — vhodné rozeslat i vykreslit. */
  onChange?: (hp: number, max: number) => void;
  onDeath?: () => void;
  onRespawn?: () => void;
}

/**
 * Životy hráče, smrt a návrat do hry.
 *
 * Vlastní životy si spravuje každý klient sám a rozesílá výsledek — ne že
 * by mu ostatní posílali, kolik má mít. Pro kooperativní svět to stačí
 * a odpadá tím kolo tam a zpět: hráč vidí ubrané životy okamžitě, ne až
 * po odezvě sítě. Cena je, že si je nikdo nemůže vynutit — kdo si upraví
 * klienta, je nesmrtelný.
 */
export class PlayerHealth {
  private current = PLAYER_MAX_HP;
  private respawnTimer = 0;
  /** Krátká nezranitelnost po návratu, ať hráče nesežerou hned znovu. */
  private invulnerable = 0;

  constructor(private readonly events: PlayerHealthEvents = {}) {}

  get hp(): number {
    return this.current;
  }

  get max(): number {
    return PLAYER_MAX_HP;
  }

  get alive(): boolean {
    return this.current > 0;
  }

  get respawnIn(): number {
    return Math.max(0, this.respawnTimer);
  }

  /** Vrací true, pokud zásah hráče zabil. */
  damage(amount: number): boolean {
    if (!this.alive || this.invulnerable > 0 || amount <= 0) return false;

    this.current = Math.max(0, this.current - amount);
    this.events.onChange?.(this.current, PLAYER_MAX_HP);

    if (this.current === 0) {
      this.respawnTimer = PLAYER_RESPAWN_SECONDS;
      this.events.onDeath?.();
      return true;
    }
    return false;
  }

  heal(amount: number): void {
    if (!this.alive) return;
    this.current = Math.min(PLAYER_MAX_HP, this.current + amount);
    this.events.onChange?.(this.current, PLAYER_MAX_HP);
  }

  update(dt: number): void {
    if (this.invulnerable > 0) this.invulnerable -= dt;
  }

  respawn(): void {
    this.current = PLAYER_MAX_HP;
    this.respawnTimer = 0;
    this.invulnerable = 2;
    this.events.onChange?.(this.current, PLAYER_MAX_HP);
    this.events.onRespawn?.();
  }
}
